import React from 'react';

import { Box } from '@mui/material';
import { DossierOpenSearch } from '@europrocurement/l2d-domain';
import { useCurrentCollectionCtx } from '@europrocurement/l2d-modules/providers';
import OrderInformations from '../OrderInformations/OrderInformations';
import DossiersCollection from '../DossiersCollection/DossiersCollection';

type CollectionInformationsTabProps = {
    source?: 'FRONT' | 'BACK';
};
const CollectionInformationsTab: React.FunctionComponent<CollectionInformationsTabProps> =
    function ({ source = 'FRONT' }) {
        const { collection } = useCurrentCollectionCtx();
        /**
         * Détermine la date de mise à jour la plus récente parmi une collection de dossiers.
         *
         * @param dossiers - Collection de dossiers contenant chacun une propriété dateMaj
         * @returns La date de mise à jour la plus récente, ou undefined si aucun dossier valide n'est trouvé
         *
         * @example
         * ```typescript
         * const dossiers = [
         *   { dateMaj: '2024-01-15' },
         *   { dateMaj: '2024-02-20' },
         *   { dateMaj: '2024-01-30' }
         * ];
         * const lastUpdate = getLastUpdateDate(dossiers); // '2024-02-20'
         * ```
         */
        const getLastUpdateDate = (dossiersFromOrder: DossierOpenSearch[]): string | undefined => {
            const validDates = dossiersFromOrder
                ?.map((dossierFromOrder) => dossierFromOrder.dateUpdate)
                .filter((date): date is string => Boolean(date));

            return validDates?.length
                ? validDates.reduce((latest, current) => (current > latest ? current : latest))
                : undefined;
        };

        /**
         * Détermine la date de création la plus ancienne parmi une collection de dossiers.
         *
         * @param dossiers - Collection de dossiers contenant chacun une propriété dateCreation
         * @returns La date de mise à jour la plus récente, ou undefined si aucun dossier valide n'est trouvé
         *
         * @example
         * ```typescript
         * const dossiers = [
         *   { dateCreation: '2024-01-15' },
         *   { dateCreation: '2024-02-20' },
         *   { dateCreation: '2024-01-30' }
         * ];
         * const firstCreate = getFirstCreateDate(dossiers); // '2024-01-15'
         * ```
         */
        const getFirstCreateDate = (dossiersFromOrder: DossierOpenSearch[]): string | undefined => {
            const validDates = dossiersFromOrder
                ?.map((dossierFromOrder) => dossierFromOrder.dateCreation)
                .filter((date): date is string => Boolean(date));

            return validDates?.length
                ? validDates.reduce((latest, current) => (current < latest ? current : latest))
                : undefined;
        };

        return (
            <Box data-testid="order-information-tab">
                {collection && (
                    <OrderInformations
                        dateCreate={getFirstCreateDate(collection.dossiers)}
                        dateUpdate={getLastUpdateDate(collection.dossiers)}
                    />
                )}
                {collection && collection.numero && collection.dossiers && (
                    <DossiersCollection
                        collectionNumber={collection.numero}
                        dossiers={
                            source === 'FRONT'
                                ? collection.dossiers.filter((d) => d.deleted === false)
                                : collection.dossiers
                        }
                        source={source}
                    />
                )}
            </Box>
        );
    };

export default CollectionInformationsTab;
